// ============================================================================
// PACKAGE ORDER SCREEN - HELPER FUNCTIONS
// ============================================================================
import React from 'react';
import PhoneIcon from '@mui/icons-material/Phone';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

import { OrderSource, OrderStatus, PaymentType, MasaSiparisi, PackageOrder, TrendyolOrder } from '../types';
import { GetirOrder, MigrosOrder, YemeksepetiOrder } from '../hooks/useSignalR';
import { getYsDeliveryKind } from './yemeksepetiDelivery';
import { normalizeMigrosStatus } from './migrosStatus';

/**
 * Sipariş kaynağını (ve sekme kimliğini) PlatformIntegrations.Platform
 * anahtarına çevirir. Ekranda 'trendyol' Trendyol Yemek demektir; pazaryeri
 * Trendyol'u ile karışmaması için eşleme elle yazıldı.
 */
export const ORDER_SOURCE_TO_PLATFORM: Record<string, string> = {
  getir: 'getir',
  migros: 'migros_yemek',
  yemeksepeti: 'yemeksepeti',
  trendyol: 'trendyol_yemek',
};

// Sipariş kaynağına göre icon döndürür
export const getSourceIcon = (source: OrderSource) => {
  switch (source) {
    case 'phone': return <PhoneIcon sx={{ fontSize: 14 }} />;
    case 'yemeksepeti': return <RestaurantMenuIcon sx={{ fontSize: 14 }} />;
    case 'trendyol': return <TwoWheelerIcon sx={{ fontSize: 14 }} />;
    case 'getir': return <ShoppingCartIcon sx={{ fontSize: 14 }} />;
    case 'migros': return <StorefrontIcon sx={{ fontSize: 14 }} />;
    case 'kiosk': return <PointOfSaleIcon sx={{ fontSize: 14 }} />;
    case 'qr': return <QrCode2Icon sx={{ fontSize: 14 }} />;
    case 'ecommerce': return <ShoppingBagIcon sx={{ fontSize: 14 }} />;
  }
};

// Sipariş kaynağına göre renk döndürür
export const getSourceColor = (source: OrderSource): string => {
  switch (source) {
    case 'phone': return '#4CAF50';
    case 'yemeksepeti': return '#FA0050';
    case 'trendyol': return '#F27A1A';
    case 'getir': return '#5D3EBC';
    case 'migros': return '#F58220';
    case 'kiosk': return '#607D8B';
    case 'qr': return '#2196F3';
    case 'ecommerce': return '#9C27B0';
  }
};

// Sipariş durumuna göre bilgi döndürür
export const getStatusInfo = (status: OrderStatus): { label: string; color: 'success' | 'warning' | 'info' | 'secondary' | 'default' | 'error'; emoji: string } => {
  switch (status) {
    case 'new': return { label: 'Yeni', color: 'warning', emoji: '🟡' };
    case 'accepted': return { label: 'Hazırlanıyor', color: 'info', emoji: '👨‍🍳' };
    case 'preparing': return { label: 'Hazırlandı', color: 'warning', emoji: '✅' };
    case 'ready': return { label: 'Teslime Hazır', color: 'success', emoji: '🟢' };
    case 'onway': return { label: 'Yolda', color: 'info', emoji: '🔵' };
    case 'delivered': return { label: 'Teslim Edildi', color: 'default', emoji: '✅' };
    case 'undelivered': return { label: 'Teslim Edilemedi', color: 'error', emoji: '⚠️' };
    case 'cancelled': return { label: 'İptal', color: 'error', emoji: '❌' };
    default: return { label: 'Bilinmiyor', color: 'default', emoji: '❓' };
  }
};

// Ödeme tipine göre bilgi döndürür
export const getPaymentInfo = (paymentType: PaymentType): { label: string; emoji: string } => {
  switch (paymentType) {
    case 'cash': return { label: 'Nakit', emoji: '💵' };
    case 'card': return { label: 'Kart', emoji: '💳' };
    case 'online': return { label: 'Online', emoji: '🌐' };
    case 'platform': return { label: 'Platform', emoji: '📱' };
  }
};

// Bekleme süresine göre renk döndürür
export const getWaitingColor = (minutes: number): string => {
  if (minutes === 0) return 'text.disabled';
  if (minutes <= 15) return 'success.main';
  if (minutes <= 30) return 'warning.main';
  if (minutes <= 45) return 'orange';
  return 'error.main';
};

// Bekleme süresini formatlı şekilde döndürür
export const formatWaitingTime = (minutes: number): string => {
  if (minutes === 0) return '-';
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}s ${mins}dk` : `${hours} saat`;
};

// Bugünün tarihi (YYYY-MM-DD formatında)
export const TODAY = new Date().toISOString().split('T')[0];

// Gece geç saatlerde çalışan şubeler için iş günü sınırı (saat)
// Gece 05:00'e kadar bir önceki günün siparişleri de gösterilir
const BUSINESS_DAY_START_HOUR = 5;

/**
 * İş günü bilgisini hesaplar (Türkiye saati).
 * Gece 00:00-05:00 arasında, bir önceki gün de "bugün" sayılır.
 *
 * KRİTİK BUG FIX (2026-05-12):
 *   Eski kod: `now.getTime() + 3*3600*1000 + now.getTimezoneOffset()*60*1000`
 *   `getTimezoneOffset()` ekleme yanlış: tarayıcı zaten Türkiye'de (GMT+3) iken
 *   `-180 dk * 60 * 1000 = -3 saat` ekliyordu → +3h ve -3h birbirini götürüyor →
 *   `turkeyHour` aslında **UTC saati** gibi davranıyordu. Sabah 07:43 GMT+3'te
 *   `turkeyHour=4` çıkıp `4 < 5 = true` olduğu için sistem "hala gece, dünü
 *   iş günü say" davranışı sergileyip dünün siparişlerini yüklüyordu.
 *
 *   Doğrusu: `Intl.DateTimeFormat` ile timezone-bağımsız Türkiye saatini almak.
 *   Bu, kullanıcının makinesinin TZ'si UTC olsa da, GMT+3 olsa da, başka bir
 *   bölgede olsa da DOĞRU Türkiye saatini verir.
 */
export function getBusinessDayInfo(): {
  today: string;
  yesterday: string | null;
  isExtendedDay: boolean;
  turkeyHour: number;
} {
  const now = new Date();
  // Türkiye saatini timezone-bağımsız hesapla (Intl.DateTimeFormat).
  // Bu, browser TZ'si ne olursa olsun gerçek 'Europe/Istanbul' saatini verir.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const turkeyYear = parseInt(get('year'), 10);
  const turkeyMonth = parseInt(get('month'), 10);
  const turkeyDay = parseInt(get('day'), 10);
  let turkeyHour = parseInt(get('hour'), 10);
  // Intl bazı locale'lerde 24'ü "24" olarak değil "00" olarak dönebilir; safety
  if (Number.isNaN(turkeyHour)) turkeyHour = 0;
  // en-CA 24-saat formatında 00..23 döner, ancak hour: '2-digit' bazı versiyonlarda
  // hour12: false'a rağmen "24" dönebilir; modulo ile garantile.
  turkeyHour = turkeyHour % 24;

  const today = `${turkeyYear}-${String(turkeyMonth).padStart(2, '0')}-${String(turkeyDay).padStart(2, '0')}`;

  if (turkeyHour < BUSINESS_DAY_START_HOUR) {
    // Bir önceki günü hesaplamak için Türkiye tarihinden Date oluştur ve -1 gün.
    // Date.UTC ile constructed Date'in getUTCDate/Month/Year değerleri kararlıdır.
    const yMs = Date.UTC(turkeyYear, turkeyMonth - 1, turkeyDay) - 24 * 60 * 60 * 1000;
    const yd = new Date(yMs);
    const yesterday = `${yd.getUTCFullYear()}-${String(yd.getUTCMonth() + 1).padStart(2, '0')}-${String(yd.getUTCDate()).padStart(2, '0')}`;
    return { today, yesterday, isExtendedDay: true, turkeyHour };
  }

  return { today, yesterday: null, isExtendedDay: false, turkeyHour };
}

/**
 * Bir tarih string'inin iş günü kapsamında olup olmadığını kontrol eder.
 * Gece 05:00 öncesinde hem bugün hem dün kabul edilir.
 */
export function isBusinessDayDate(orderDate: string): boolean {
  const { today, yesterday } = getBusinessDayInfo();
  return orderDate === today || (yesterday !== null && orderDate === yesterday);
}

// ============================================================================
// MASA SİPARİŞİ DÖNÜŞTÜRME FONKSİYONLARI
// ============================================================================

/**
 * MasaSiparisi'nden kaynak tipini belirle
 */
export const getOrderSourceFromMasaSiparisi = (siparis: MasaSiparisi): 'qr' | 'ecommerce' | 'kiosk' | null => {
  const kaynak = siparis.siparisKaynagi?.toLowerCase() || '';
  
  // Restoran / Hızlı Satış siparişleri paket ekranına ait değil
  if (kaynak === 'restaurantquicksale' || kaynak === 'quicksale') {
    return null;
  }
  if (siparis.siparisId?.startsWith('RS-') || siparis.siparisId?.includes('-HS-')) {
    return null;
  }
  
  if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce')) {
    return 'ecommerce';
  } else if (kaynak.includes('kiosk')) {
    return 'kiosk';
  } else {
    return 'qr';
  }
};

/**
 * MasaSiparisi'ni PackageOrder formatına dönüştür
 */
export const convertMasaSiparisiToPackageOrder = (siparis: MasaSiparisi): PackageOrder => {
  const source = getOrderSourceFromMasaSiparisi(siparis) || 'qr';
  
  // Sipariş durumunu map et
  let status: OrderStatus = 'new';
  const durum = siparis.siparisDurumu?.toLowerCase() || '';
  if (durum === 'bekliyor' || durum === 'pending' || durum === 'yeni') {
    status = 'new';
  } else if (durum === 'hazirlaniyor' || durum === 'preparing' || durum === 'hazır' || durum === 'ready') {
    status = 'ready';
  } else if (durum === 'yolda' || durum === 'onway' || durum === 'delivering') {
    status = 'onway';
  } else if (durum === 'teslim edildi' || durum === 'delivered' || durum === 'tamamlandı') {
    status = 'delivered';
  } else if (durum === 'iptal' || durum === 'cancelled') {
    status = 'cancelled';
  }
  
  // Sipariş zamanını parse et
  const orderDate = siparis.siparisOlusturmaTarihi?.split('T')[0] || new Date().toISOString().split('T')[0];
  const orderTime = siparis.siparisOlusturmaTarihi?.split('T')[1]?.substring(0, 5) || new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  
  // ✅ Bekleme süresini hesapla:
  // - Teslim edilmiş siparişler için: Güncelleme tarihi - Oluşturma tarihi (sabit)
  // - Aktif siparişler için: Şu an - Oluşturma tarihi (dinamik)
  const createdAt = new Date(siparis.siparisOlusturmaTarihi);
  let waitingMinutes: number;
  
  if (status === 'delivered' || status === 'cancelled') {
    // Teslim edildi veya iptal edildi - bekleme süresi sabit
    if (siparis.siparisGuncellenmeTarihi) {
      const completedAt = new Date(siparis.siparisGuncellenmeTarihi);
      waitingMinutes = Math.floor((completedAt.getTime() - createdAt.getTime()) / 60000);
    } else {
      // Güncelleme tarihi yoksa şu anki süreyi kullan
      waitingMinutes = Math.floor((Date.now() - createdAt.getTime()) / 60000);
    }
  } else {
    // Aktif sipariş - dinamik süre
    waitingMinutes = Math.floor((Date.now() - createdAt.getTime()) / 60000);
  }
  
  return {
    id: siparis.id,
    shortOrderNo: siparis.siparisId,
    externalOrderNo: siparis.siparisId,
    source: source,
    customerName: siparis.musteriAdi || siparis.kullaniciAdi || 'Anonim',
    customerPhone: siparis.musteriTelefon || '',
    // QR Menü siparişlerinde masa adını öncelikli göster
    customerAddress: siparis.masaAdi || (siparis.masaNumarasi ? `Masa ${siparis.masaNumarasi}` : siparis.musteriAdres || ''),
    orderDate: orderDate,
    orderTime: orderTime,
    totalAmount: siparis.toplamTutar || 0,
    status: status,
    itemCount: siparis.siparisDetaylari?.reduce((sum, d) => sum + (d.adet || 1), 0) || 0,
    waitingMinutes: waitingMinutes,
    paymentType: 'online',
    isPaid: false,
    totalPaid: 0,
    // ✅ YENİ: Tarih bilgileri
    createdAt: siparis.siparisOlusturmaTarihi,
    completedAt: (status === 'delivered' || status === 'cancelled') ? siparis.siparisGuncellenmeTarihi : undefined,
    // QR / e-ticaret / kiosk siparişlerinin platform mağazası yoktur; marka
    // alanları bilerek boş bırakılır, tablo bu satırlarda tire gösterir.
  };
};

// ============================================================================
// GETİR SİPARİŞ DÖNÜŞTÜRME FONKSİYONU
// ============================================================================

/**
 * Getir siparişini PackageOrder formatına dönüştür
 * RESMİ Getir Status Kodları:
 * 325 = İleri tarihli sipariş, ön onay bekliyor
 * 350 = İleri tarihli sipariş, ön onay alındı
 * 400 = Restoran onayı bekleniyor
 * 500 = Sipariş hazırlanıyor (verify sonrası)
 * 550 = Sipariş hazırlandı (prepare sonrası, *Getir Getirsin)
 * 600 = Sipariş kuryeye teslim edildi (*Getir Getirsin)
 * 700 = Kurye yola çıktı
 * 800 = Kurye adrese ulaştı (*Getir Getirsin)
 * 900 = Sipariş teslim edildi
 * 1500 = Sipariş admin tarafından iptal edildi
 * 1600 = Sipariş restoran tarafından iptal edildi
 */
export const convertGetirOrderToPackageOrder = (order: GetirOrder): PackageOrder => {
  // Getir durumunu PackageOrder status'una map et (Resmi Getir Dokümantasyonu)
  // 325=İleri tarihli ön onay bekleniyor, 350=İleri tarihli onaylandı
  // 400=Restoran onayı bekleniyor (YENİ SİPARİŞ)
  // 500=Hazırlanıyor (verify sonrası), 550=Hazırlandı (prepare sonrası)
  // 600=Kuryeye teslim (Getir Getirsin), 700=Kurye yolda, 800=Adrese ulaştı
  // 900=Teslim edildi, 1500=Admin iptal, 1600=Restoran iptal
  let status: OrderStatus = 'new';
  if (order.status === 1500 || order.status === 1600) {
    status = 'cancelled';
  } else if (order.status >= 900) {
    status = 'delivered';
  } else if (order.status >= 700) {
    status = 'onway';
  } else if (order.status >= 600) {
    status = 'onway';
  } else if (order.status >= 550) {
    status = 'preparing'; // Hazırlandı (prepare sonrası)
  } else if (order.status >= 500) {
    status = 'accepted'; // Hazırlanıyor (verify sonrası)
  } else if (order.status >= 400) {
    status = 'new'; // 400=Restoran onayı bekleniyor → YENİ
  } else if (order.status >= 350) {
    status = 'accepted'; // 350=İleri tarihli onaylandı
  } else {
    status = 'new'; // 325=İleri tarihli bekleniyor
  }
  
  // Getir ödeme tipleri:
  // 1=Online Ödeme, 3=Kapıda Kart, 4=Kapıda Nakit
  // 5+=Yemek Kartları (Sodexo, Pluxee, Multinet, Metropol, SetCard, Ticket vb.)
  // 5+ kodlu kartların "Online" olanları online ödeme, "Kapıda" olanları kart ödemesi
  let paymentType: PaymentType = 'online';
  if (order.paymentMethod === 4) {
    paymentType = 'cash';
  } else if (order.paymentMethod === 3) {
    paymentType = 'card';
  } else if (order.paymentMethod === 1) {
    paymentType = 'online';
  } else if ((order.paymentMethod ?? 0) >= 5) {
    const ptText = (order.paymentMethodText || '').toLowerCase();
    if (ptText.includes('nakit') || ptText.includes('cash')) {
      paymentType = 'cash';
    } else if (ptText.includes('online') || ptText.includes('çevrimiçi')) {
      paymentType = 'online';
    } else if (ptText.includes('kapıda') || ptText.includes('door')) {
      paymentType = 'card';
    } else {
      paymentType = 'online';
    }
  }
  
  // Tarih bilgileri
  const orderDateStr = order.checkoutDate || order.createdAt;
  const orderDate = orderDateStr?.split('T')[0] || new Date().toISOString().split('T')[0];
  const orderTime = orderDateStr?.split('T')[1]?.substring(0, 5) || new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  
  // Bekleme süresi
  const createdAt = new Date(orderDateStr || Date.now());
  let waitingMinutes: number;
  
  if (status === 'delivered' || status === 'cancelled') {
    if (order.updatedAt) {
      const completedAt = new Date(order.updatedAt);
      waitingMinutes = Math.floor((completedAt.getTime() - createdAt.getTime()) / 60000);
    } else {
      waitingMinutes = Math.floor((Date.now() - createdAt.getTime()) / 60000);
    }
  } else {
    waitingMinutes = Math.floor((Date.now() - createdAt.getTime()) / 60000);
  }
  
  return {
    id: order.id,
    shortOrderNo: order.confirmationId || order.getirId?.substring(0, 8) || `G-${order.id}`,
    externalOrderNo: order.getirId,
    source: 'getir',
    customerName: order.clientName || 'Getir Müşterisi',
    customerPhone: order.clientPhone || '',
    customerAddress: order.deliveryAddress || `${order.district || ''} ${order.city || ''}`.trim(),
    orderDate: orderDate,
    orderTime: orderTime,
    totalAmount: (order.totalDiscountedPrice != null && order.totalDiscountedPrice > 0 && order.totalDiscountedPrice !== order.totalPrice)
      ? order.totalDiscountedPrice : (order.totalPrice || 0),
    status: status,
    itemCount: order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || order.itemCount || 0,
    waitingMinutes: waitingMinutes,
    paymentType: paymentType,
    paymentMethod: order.paymentMethodText,
    isPaid: !!order.invoiceId,
    isOnlinePayment: paymentType === 'online',
    totalPaid: order.invoiceId 
      ? ((order.totalDiscountedPrice != null && order.totalDiscountedPrice > 0 && order.totalDiscountedPrice !== order.totalPrice)
          ? order.totalDiscountedPrice : (order.totalPrice || 0))
      : 0,
    // Kurye sütunu: Type 1 → "Getir Getirsin", Type 2 → assignedCourierName öncelikli (Getir'in courierName'i değil)
    courierName: order.deliveryType === 1
      ? 'Getir Getirsin'
      : (order.assignedCourierName || 
         (order.courierName && !order.courierName.toLowerCase().includes('restorankuryesi') && order.courierName.toLowerCase() !== 'restoran kuryesi'
          ? order.courierName 
          : undefined)),
    createdAt: order.createdAt,
    completedAt: (status === 'delivered' || status === 'cancelled') ? order.updatedAt : undefined,
    deliveryType: order.deliveryType,
    deliveryTypeName: order.deliveryType === 1 ? 'Getir Getirsin' : order.deliveryType === 2 ? 'Restoran Getirsin' : undefined,
    getirStatus: order.status,
    isPreOrder: order.isScheduled || false,
    expectedDeliveryTime: order.scheduledDate || undefined,
    // Çoklu mağaza: marka damgası. Boşsa tablo mağaza kimliğinden çözümler.
    platformIntegrationId: order.platformIntegrationId,
    storeName: order.storeName || undefined,
    externalStoreId: order.restaurantId || undefined,
  };
};

// ============================================================================
// MIGROS SIPARIS DONUSTURME FONKSIYONU
// ============================================================================

export const convertMigrosOrderToPackageOrder = (order: MigrosOrder): PackageOrder => {
  // Migros yazarken ve okurken farklı statü sözlükleri kullanıyor; eşleme
  // migrosStatus.ts'de. Tanınmayan statü 'new' SAYILMAMALI: onaylanmış sipariş
  // yeniden onaya düşüyor ve Migros ikinci onay isteğini reddediyor.
  let status: OrderStatus;
  switch (normalizeMigrosStatus(order.status)) {
    case 'NEW_PENDING': status = 'new'; break;
    case 'APPROVED': status = 'accepted'; break;
    case 'PREPARED': status = 'preparing'; break;
    case 'DELIVERY': status = 'onway'; break;
    case 'COMPLETED': status = 'delivered'; break;
    case 'REJECTED':
    case 'CANCELLED': status = 'cancelled'; break;
    // Bilinmeyen statü: siparişi "işlemde" gösterip onay butonunu kapatıyoruz.
    default: status = 'accepted'; break;
  }

  let paymentType: PaymentType = 'online';
  if (!order.isOnlinePayment) {
    const pt = (order.paymentType || '').toLowerCase();
    if (pt.includes('nakit') || pt.includes('cash')) paymentType = 'cash';
    else paymentType = 'card';
  }

  // Migros createdAsMs alanı epoch (ms) cinsinden geliyor - saat dilimi belirsizliği yok
  const createdAtMs = order.createdAsMs && order.createdAsMs > 0
    ? order.createdAsMs
    : new Date(order.createdAt || Date.now()).getTime();

  const turkeyDate = new Date(createdAtMs + 3 * 60 * 60 * 1000);
  const orderDate = `${turkeyDate.getUTCFullYear()}-${String(turkeyDate.getUTCMonth()+1).padStart(2,'0')}-${String(turkeyDate.getUTCDate()).padStart(2,'0')}`;
  const orderTime = `${String(turkeyDate.getUTCHours()).padStart(2,'0')}:${String(turkeyDate.getUTCMinutes()).padStart(2,'0')}`;

  let waitingMinutes: number;
  if (status === 'delivered' || status === 'cancelled') {
    if (order.updatedAt) {
      waitingMinutes = Math.floor((new Date(order.updatedAt).getTime() - createdAtMs) / 60000);
    } else {
      waitingMinutes = Math.floor((Date.now() - createdAtMs) / 60000);
    }
  } else {
    waitingMinutes = Math.floor((Date.now() - createdAtMs) / 60000);
  }
  if (waitingMinutes < 0) waitingMinutes = 0;

  const totalAmountTL = (order.discountedPrice || order.totalPrice || 0) / 100;

  return {
    id: order.id,
    shortOrderNo: order.shortCode || `M-${order.migrosOrderId}`,
    externalOrderNo: String(order.migrosOrderId),
    source: 'migros',
    customerName: order.customerFullName || 'Migros Musterisi',
    customerPhone: order.customerPhoneNumber || '',
    customerAddress: [order.addressDetail, order.districtName, order.townName, order.cityName].filter(Boolean).join(', '),
    orderDate,
    orderTime,
    totalAmount: totalAmountTL,
    status,
    itemCount: order.items?.reduce((sum, item) => sum + (item.amount || 1), 0) || 0,
    waitingMinutes,
    paymentType,
    paymentMethod: order.paymentType,
    isPaid: !!order.invoiceId,
    totalPaid: order.invoiceId ? totalAmountTL : 0,
    courierName: order.deliveryProvider === 'MIGROS'
      ? 'Migros Kurye'
      : (order.assignedCourierName || undefined),
    createdAt: order.createdAt,
    completedAt: (status === 'delivered' || status === 'cancelled') ? order.updatedAt : undefined,
    migrosStatus: order.status,
    migrosDeliveryProvider: order.deliveryProvider,
    isOnlinePayment: order.isOnlinePayment,
    migrosOrderId: order.id,
    migrosExternalId: order.migrosOrderId,
    // Çoklu mağaza: order.storeName Migros'un kendi mağaza adı olduğu için
    // marka adı integrationStoreName alanından okunur.
    platformIntegrationId: order.platformIntegrationId,
    storeName: order.integrationStoreName || undefined,
    externalStoreId: order.storeId ? String(order.storeId) : undefined,
  };
};

// ============================================================================
// YEMEKSEPETI SIPARIS DONUSTURME FONKSIYONU
// ============================================================================

export const convertYemeksepetiOrderToPackageOrder = (order: YemeksepetiOrder): PackageOrder => {
  let status: OrderStatus = 'new';
  const ysStatus = (order.status || '').toUpperCase();
  if (ysStatus === 'PENDING' || ysStatus === 'NEW') status = 'new';
  else if (ysStatus === 'ACCEPTED' || ysStatus === 'CONFIRMED') status = 'accepted';
  else if (ysStatus === 'PREPARED' || ysStatus === 'READY') status = 'preparing';
  else if (ysStatus === 'PICKED_UP' || ysStatus === 'ON_DELIVERY' || ysStatus === 'DELIVERY') status = 'onway';
  else if (ysStatus === 'DELIVERED' || ysStatus === 'COMPLETED') status = 'delivered';
  else if (ysStatus === 'REJECTED' || ysStatus === 'CANCELLED') status = 'cancelled';

  let paymentType: PaymentType = 'online';
  const pt = (order.paymentType || '').toUpperCase();
  const ptNormalized = pt.replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G').replace(/Ü/g, 'U').replace(/Ö/g, 'O').replace(/Ç/g, 'C');
  const isDoorPayment = ptNormalized.includes('KAPIDA') || ptNormalized.includes('DOOR') || ptNormalized.includes('ON_DELIVERY');
  const isCashPayment = ptNormalized.includes('NAKIT') || ptNormalized.includes('CASH');
  const isCardPayment = ptNormalized.includes('KREDI') || ptNormalized.includes('BANKA') || ptNormalized.includes('CREDIT') || ptNormalized.includes('CARD');
  const isOnlinePayment = ptNormalized.includes('ONLINE') ||
    ptNormalized.includes('CREDIT_CARD_ONLINE') ||
    ptNormalized === 'PAY_WITH_CARD' ||
    ptNormalized.startsWith('PAY_WITH');

  if (isOnlinePayment) {
    paymentType = 'online';
  } else if (isDoorPayment && isCashPayment) {
    paymentType = 'cash';
  } else if (isDoorPayment && isCardPayment) {
    paymentType = 'card';
  } else if (isDoorPayment) {
    paymentType = 'card';
  } else if (isCashPayment) {
    paymentType = 'cash';
  } else if (isCardPayment) {
    paymentType = 'card';
  }

  const isOnline = paymentType === 'online';
  const gtsPaymentRecorded = !!order.invoiceId;

  const orderDateStr = order.createdAt || new Date().toISOString();
  const createdAtUtc = orderDateStr.endsWith('Z') ? new Date(orderDateStr) : new Date(orderDateStr + 'Z');
  const createdAtMs = createdAtUtc.getTime();

  const turkeyDate = new Date(createdAtMs + 3 * 60 * 60 * 1000);
  const orderDate = `${turkeyDate.getUTCFullYear()}-${String(turkeyDate.getUTCMonth()+1).padStart(2,'0')}-${String(turkeyDate.getUTCDate()).padStart(2,'0')}`;
  const orderTime = `${String(turkeyDate.getUTCHours()).padStart(2,'0')}:${String(turkeyDate.getUTCMinutes()).padStart(2,'0')}`;

  let waitingMinutes: number;
  if (status === 'delivered' || status === 'cancelled') {
    if (order.updatedAt) {
      const updatedUtc = order.updatedAt.endsWith('Z') ? new Date(order.updatedAt) : new Date(order.updatedAt + 'Z');
      waitingMinutes = Math.floor((updatedUtc.getTime() - createdAtMs) / 60000);
    } else {
      waitingMinutes = Math.floor((Date.now() - createdAtMs) / 60000);
    }
  } else {
    waitingMinutes = Math.floor((Date.now() - createdAtMs) / 60000);
  }
  if (waitingMinutes < 0) waitingMinutes = 0;

  const totalAmount = order.grandTotal || 0;

  return {
    id: order.id,
    shortOrderNo: order.shortCode || order.code || `YS-${order.id}`,
    externalOrderNo: order.remoteOrderId || order.token,
    source: 'yemeksepeti',
    customerName: order.customerFullName || `${order.customerFirstName || ''} ${order.customerLastName || ''}`.trim() || 'Yemeksepeti Müşterisi',
    customerPhone: order.customerPhone || '',
    customerAddress: [order.deliveryAddress, order.deliveryDistrict, order.deliveryCity].filter(Boolean).join(', '),
    orderDate,
    orderTime,
    totalAmount,
    status,
    itemCount: order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0,
    waitingMinutes,
    paymentType,
    paymentMethod: order.paymentType,
    isPaid: gtsPaymentRecorded,
    totalPaid: gtsPaymentRecorded ? totalAmount : 0,
    // Kurye sütunu: Gel Al'da kurye yoktur, YS kendi kuryesiyle taşıyorsa atama yapılmaz.
    courierName: (() => {
      const kind = getYsDeliveryKind(order);
      if (kind === 'pickup') return 'Gel Al (Müşteri Alacak)';
      if (kind === 'platformCourier') return 'Yemeksepeti Kurye';
      return order.assignedCourierName || undefined;
    })(),
    createdAt: turkeyDate.toISOString(),
    completedAt: (status === 'delivered' || status === 'cancelled') ? (order.completedAt || order.updatedAt) : undefined,
    yemeksepetiStatus: order.status,
    yemeksepetiExpeditionType: order.expeditionType,
    yemeksepetiOrderType: order.orderType,
    isOnlinePayment: isOnline,
    isPreOrder: order.isPreOrder || false,
    expectedDeliveryTime: order.expectedDeliveryTime,
    // Çoklu mağaza: YS'de dış mağaza kimliği platformRestaurantId (remoteId).
    platformIntegrationId: order.platformIntegrationId,
    storeName: order.storeName || undefined,
    externalStoreId: order.platformRestaurantId || order.remoteId || undefined,
  };
};

// ============================================================================
// TRENDYOL YEMEK SİPARİŞ DÖNÜŞTÜRME FONKSİYONU
// ============================================================================

/**
 * Siparişin satıcı tarafından karşılanan toplam indirimi.
 *
 * Trendyol kampanyayı `promotions[]`, kuponu (ör. "Plus Yemek Kuponu") `coupon`
 * alanında bildiriyor ve bir siparişte genelde yalnızca biri dolu geliyor; ikisi
 * de okunmazsa müşteriden tahsil edilen tutar değil brüt tutar hesaplanır.
 * Paket seviyesindeki kupon yoksa aynı kupon satır bazında
 * `items[].coupon.amount.seller` olarak geldiği için oradan toplanır; iki alan
 * aynı indirimi tekrarladığından ikisi birden sayılmaz.
 */
export const calculateTrendyolSellerDiscount = (
  order: Pick<TrendyolOrder, 'promotions' | 'coupon' | 'lines'>,
): number => {
  const promotionDiscount = (order.promotions || []).reduce(
    (sum, p) => sum + (p?.totalSellerAmount || 0),
    0,
  );

  let couponDiscount = Number(order.coupon?.totalSellerAmount || 0);
  if (couponDiscount <= 0) {
    couponDiscount = (order.lines || []).reduce(
      (lineSum, line) => lineSum + (line.items || []).reduce(
        (itemSum, item) => item.isCancelled
          ? itemSum
          : itemSum + Number(item.coupon?.amount?.seller || 0),
        0,
      ),
      0,
    );
  }

  const total = promotionDiscount + couponDiscount;
  return total > 0 ? total : 0;
};

export const convertTrendyolOrderToPackageOrder = (order: TrendyolOrder): PackageOrder => {
  let status: OrderStatus = 'new';
  const tStatus = (order.packageStatus || '').toLowerCase();
  if (tStatus === 'created') status = 'new';
  else if (tStatus === 'picking') status = 'accepted';
  else if (tStatus === 'invoiced') status = 'preparing';
  else if (tStatus === 'shipped') status = 'onway';
  else if (tStatus === 'delivered') status = 'delivered';
  else if (tStatus === 'unsupplied' || tStatus === 'cancelled' || tStatus === 'returned') status = 'cancelled';

  const isGoDelivery = order.deliveryType === 'GO';

  let paymentType: PaymentType = 'cash';
  if (isGoDelivery) {
    paymentType = 'online';
  } else {
    const pt = order.payment?.paymentType || '';
    if (pt === 'PAY_WITH_ON_DELIVERY') {
      const onDeliveryType = order.payment?.onDelivery?.paymentType || '';
      if (onDeliveryType === 'CASH') paymentType = 'cash';
      else paymentType = 'card';
    } else {
      paymentType = 'online';
    }
  }

  const creationDate = new Date(order.packageCreationDate);
  const orderDate = `${creationDate.getFullYear()}-${String(creationDate.getMonth() + 1).padStart(2, '0')}-${String(creationDate.getDate()).padStart(2, '0')}`;
  const orderTime = `${String(creationDate.getHours()).padStart(2, '0')}:${String(creationDate.getMinutes()).padStart(2, '0')}`;

  let waitingMinutes = Math.floor((Date.now() - order.packageCreationDate) / 60000);
  if (status === 'delivered' || status === 'cancelled') {
    waitingMinutes = Math.floor((order.packageModificationDate - order.packageCreationDate) / 60000);
  }
  if (waitingMinutes < 0) waitingMinutes = 0;

  const itemCount = order.lines?.reduce((sum, line) => sum + (line.items?.length || 1), 0) || 0;
  const customerName = order.customer
    ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim()
    : 'Trendyol Müşterisi';

  const addressParts = [];
  if (order.address?.address1) addressParts.push(order.address.address1);
  if (order.address?.neighborhood) addressParts.push(order.address.neighborhood);
  if (order.address?.district) addressParts.push(order.address.district);
  if (order.address?.city) addressParts.push(order.address.city);
  if (order.address?.addressDescription) addressParts.push(`(${order.address.addressDescription})`);
  if (order.address?.floor) addressParts.push(`Kat: ${order.address.floor}`);
  if (order.address?.doorNumber) addressParts.push(`Kapı: ${order.address.doorNumber}`);

  const isOnline = paymentType === 'online';
  const gtsPaymentRecorded = !!order.gtsPaid;

  const rawTotal = order.totalPrice || 0;
  const grandTotal = rawTotal - calculateTrendyolSellerDiscount(order);

  return {
    id: parseInt(order.orderId) || Math.abs(hashCode(order.id)),
    shortOrderNo: order.orderCode || `TRD-${order.orderNumber || order.orderId}`,
    externalOrderNo: order.orderNumber || order.orderId,
    source: 'trendyol',
    customerName,
    customerPhone: order.address?.phone || order.callCenterPhone || '',
    customerAddress: addressParts.join(', '),
    orderDate,
    orderTime,
    totalAmount: grandTotal,
    status,
    itemCount,
    waitingMinutes,
    paymentType,
    isPaid: gtsPaymentRecorded,
    totalPaid: gtsPaymentRecorded ? grandTotal : 0,
    courierName: order.deliveryType === 'GO' ? 'Trendyol Kurye' : undefined,
    createdAt: creationDate.toISOString(),
    completedAt: (status === 'delivered' || status === 'cancelled')
      ? new Date(order.packageModificationDate).toISOString()
      : undefined,
    trendyolStatus: order.packageStatus,
    trendyolDeliveryType: order.deliveryType,
    trendyolPackageId: order.id,
    trendyolOrderId: order.orderId,
    isOnlinePayment: isOnline,
    // Çoklu mağaza damgası
    platformIntegrationId: order.platformIntegrationId,
    storeName: order.storeName || undefined,
    externalStoreId: order.storeId ? String(order.storeId) : undefined,
  };
};

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash;
}

/**
 * Trendyol ham order objesinden, `convertTrendyolToPackageOrder` ile aynı
 * mantıkla normalize edilmiş `PackageOrder.id` (numeric) üretir.
 *
 * Bu helper, ham Trendyol siparişini elde edip `PackageOrder` listesinde
 * eşleştirme yapması gereken her yerde (örn. detay modalindeki "Online Fiş
 * Yazdır" akışı) `PackageOrder.id` ile %100 aynı sonucu verir. Aksi halde
 * `order.id` (Trendyol internal hash) kullanılır ve liste içinde eşleşme
 * bulunamaz (bkz. PackageOrderScreen `onRequestOnlineReceipt`).
 */
export function getTrendyolPackageOrderId(order: { orderId?: string | number; id?: string | number } | null | undefined): number | null {
  if (!order) return null;
  const parsed = parseInt(String(order.orderId ?? ''), 10);
  if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  if (order.id !== undefined && order.id !== null) {
    return Math.abs(hashCode(String(order.id)));
  }
  return null;
}

// ============================================================================
// YEMEKSEPETI v2026 (15.05.2026) - DISCOUNT ADI VE SPONSOR CEVIRI YARDIMCILARI
// ============================================================================
// Yemeksepeti yeni payload'da indirim adlari sadece 3 sabit baslik altinda
// gelir: "Food Discount" | "Delivery Fee Discount" | "Loyalty"
// Sponsor enum'u: "PLATFORM" | "VENDOR" | "THIRD_PARTY"
// Bilinmeyen ad/sponsor "olduğu gibi" gösterilir (defansif).

export const translateYsDiscountName = (name: string): string => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('food')) return 'Yemek Indirimi';
  if (lower.includes('delivery')) return 'Teslimat Ucreti Indirimi';
  if (lower.includes('loyalty')) return 'Sadakat Indirimi';
  return name || 'Indirim';
};

export const translateYsSponsor = (sponsor: string): string => {
  switch ((sponsor || '').toUpperCase()) {
    case 'PLATFORM': return 'Yemeksepeti';
    case 'VENDOR': return 'Restoran';
    case 'THIRD_PARTY': return '3. Taraf';
    default: return sponsor || '';
  }
};
